import React, { useEffect, useRef, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Link as MuiLink,
  Paper,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  type MarketingRouterTreatmentType,
  getMarketingRouterTreatmentConfig,
  marketingRouterTreatmentTypes,
  splitTreatmentFieldValues,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';
import { getCountryObjs } from '@luxuryescapes/lib-countries';

import CopyableId from '~/components/Common/Elements/CopyableId';

import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import MarketingRouterPromoDisplayConfigField from './MarketingRouterPromoDisplayConfigField';
import MarketingRouterTermLabel from './MarketingRouterTermLabel';
import { getTreatmentLinkPath } from './marketingRouterTreatmentDisplay';
import {
  type MarketingRouterTreatmentDraft,
  type MarketingRouterTreatmentFieldName,
  getTreatmentFieldValueForDraft,
} from './marketingRouterTreatments';

type Variant = Omit<MarketingRouterRule['variants'][number], 'treatments'> & {
  treatments?: Array<MarketingRouterTreatmentDraft>;
};

type CountryOption = { code: string; label: string };

// Options for treatment fields with `format: 'country-code'`; the value stored on the
// treatment is the ISO code (comma-separated when the field is `multiple`).
const countryOptions: Array<CountryOption> = Object.values(getCountryObjs())
  .map((country) => ({ code: country.iso2, label: `${country.countryName} (${country.iso2})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

function toCountryOptions(value: string): Array<CountryOption> {
  return splitTreatmentFieldValues(value).map(
    (code) => countryOptions.find((option) => option.code === code) ?? { code, label: code },
  );
}

export type MarketingRouterVariantCardProps = {
  variant: Variant;
  index: number;
  canRemove: boolean;
  showFieldDescriptions: boolean;
  onFieldChange: (field: 'variant_name' | 'weight', value: string) => void;
  onMakeControl: () => void;
  onRemove: () => void;
  onAddTreatment: () => void;
  onTreatmentTypeChange: (treatmentIndex: number, type: MarketingRouterTreatmentType) => void;
  onTreatmentFieldChange: (treatmentIndex: number, field: MarketingRouterTreatmentFieldName, value: string) => void;
  onRemoveTreatment: (treatmentIndex: number) => void;
};

/** What a treatment does, in one line: its type, then the fields that are set. */
export function getTreatmentSummary(treatment: MarketingRouterTreatmentDraft): string {
  const config = getMarketingRouterTreatmentConfig(treatment.type);
  const fields =
    config?.fields
      ?.map((field) => {
        const value = getTreatmentFieldValueForDraft(treatment, field.field);
        return value ? `${field.fieldName}: ${value}` : null;
      })
      .filter((field): field is string => field != null) ?? [];

  return fields.length
    ? `${config?.typeName ?? treatment.type} — ${fields.join(', ')}`
    : (config?.typeName ?? treatment.type);
}

function renderTreatmentSummary(treatment: MarketingRouterTreatmentDraft): React.ReactNode {
  const summary = getTreatmentSummary(treatment);
  const treatmentName = getMarketingRouterTreatmentConfig(treatment.type)?.typeName ?? treatment.type;
  const linkPath = getTreatmentLinkPath(treatment);

  if (!linkPath) {
    return summary;
  }

  return (
    <>
      <MuiLink
        component={Link}
        to={linkPath}
        underline="hover"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {treatmentName}
      </MuiLink>
      {summary.slice(treatmentName.length)}
    </>
  );
}

/**
 * One variant: its identity and split on the header, its treatments below.
 *
 * A treatment reads as a summary line until it is opened, so a variant fits on
 * one screen and the fields on show are the ones being edited. A treatment
 * that has just been added opens on its own — it is empty, so its summary
 * would say nothing.
 */
export default function MarketingRouterVariantCard({
  variant,
  index,
  canRemove,
  showFieldDescriptions,
  onFieldChange,
  onMakeControl,
  onRemove,
  onAddTreatment,
  onTreatmentTypeChange,
  onTreatmentFieldChange,
  onRemoveTreatment,
}: MarketingRouterVariantCardProps) {
  const treatments = variant.treatments ?? [];
  const [expandedTreatmentIndices, setExpandedTreatmentIndices] = useState<Array<number>>([]);
  const previousTreatmentCount = useRef(treatments.length);

  useEffect(() => {
    if (treatments.length > previousTreatmentCount.current) {
      setExpandedTreatmentIndices((current) => [...current, treatments.length - 1]);
    }
    previousTreatmentCount.current = treatments.length;
  }, [treatments.length]);

  const toggleTreatment = (treatmentIndex: number) => {
    setExpandedTreatmentIndices((current) =>
      current.includes(treatmentIndex)
        ? current.filter((expanded) => expanded !== treatmentIndex)
        : [...current, treatmentIndex],
    );
  };

  return (
    <Card variant="outlined">
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="subtitle2">Variant {index + 1}</Typography>
        {variant.is_control ? <Chip label="Control" size="small" color="primary" variant="outlined" /> : null}
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {variant.weight}% of traffic
        </Typography>
        <Tooltip title={canRemove ? 'Remove variant' : 'A rule needs at least one variant'}>
          <span>
            <IconButton
              size="small"
              aria-label={`Remove variant ${index + 1}`}
              onClick={onRemove}
              disabled={!canRemove}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Variant name"
              required
              value={variant.variant_name}
              onChange={(event) => onFieldChange('variant_name', event.target.value)}
              sx={{ flex: '1 1 260px' }}
            />
            <TextField
              label={<MarketingRouterTermLabel term="weight">Weight</MarketingRouterTermLabel>}
              required
              type="number"
              inputProps={{ min: 0, max: 100, step: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              value={variant.weight}
              onChange={(event) => onFieldChange('weight', event.target.value)}
              sx={{ flex: '0 0 140px' }}
            />
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 0 auto' }}>
              <Radio
                checked={variant.is_control}
                onChange={onMakeControl}
                inputProps={{ 'aria-label': `Make variant ${index + 1} the control` }}
              />
              <Typography variant="body2">Control</Typography>
            </Stack>
          </Stack>

          {variant.variant_id ? (
            <CopyableId
              name="Variant ID"
              value={variant.variant_id}
              showName
              variant="caption"
              color="text.secondary"
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              Variant ID: assigned when the rule is saved
            </Typography>
          )}

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Treatments</Typography>
            {treatments.length ? (
              treatments.map((treatment, treatmentIndex) => {
                const config = getMarketingRouterTreatmentConfig(treatment.type);
                const isExpanded = expandedTreatmentIndices.includes(treatmentIndex);

                return (
                  <Paper key={`${treatment.type}-${treatmentIndex}`} variant="outlined">
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleTreatment(treatmentIndex)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleTreatment(treatmentIndex);
                        }
                      }}
                      aria-expanded={isExpanded}
                      sx={{
                        alignItems: 'center',
                        background: 'none',
                        border: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        font: 'inherit',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        textAlign: 'left',
                        width: '100%',
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {renderTreatmentSummary(treatment)}
                      </Typography>
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          color: 'action.active',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: (theme) =>
                            theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
                        }}
                      />
                    </Box>
                    <Collapse in={isExpanded} unmountOnExit>
                      <Divider />
                      <Stack spacing={1} sx={{ p: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                          <TextField
                            label="Type"
                            select
                            value={treatment.type}
                            sx={{ flex: '1 1 220px' }}
                            onChange={(event) => {
                              const nextType = marketingRouterTreatmentTypes.find(
                                (type) => type === event.target.value,
                              );
                              if (nextType) {
                                onTreatmentTypeChange(treatmentIndex, nextType);
                              }
                            }}
                          >
                            {marketingRouterTreatmentTypes.map((type) => (
                              <MenuItem key={type} value={type}>
                                {getMarketingRouterTreatmentConfig(type).typeName}
                              </MenuItem>
                            ))}
                          </TextField>
                          {config?.fields?.map((field) => {
                            const label = field.required ? field.fieldName : `${field.fieldName} (optional)`;
                            const helperText = showFieldDescriptions ? field.longDescription : undefined;
                            const value = getTreatmentFieldValueForDraft(treatment, field.field);

                            // An id the contract marks as a promo display config renders as a
                            // picker over the brand's active configs; the treatment stores the id.
                            if (field.references === 'promo-display-config') {
                              return (
                                <MarketingRouterPromoDisplayConfigField
                                  key={field.field}
                                  value={value}
                                  label={label}
                                  required={field.required}
                                  description={helperText}
                                  onChange={(promoDisplayConfigId) =>
                                    onTreatmentFieldChange(treatmentIndex, field.field, promoDisplayConfigId)
                                  }
                                />
                              );
                            }

                            // Country fields render as a country picker; `multiple` ones store the
                            // selected ISO codes as one comma-separated value, as the contract expects.
                            if (field.format === 'country-code') {
                              const selectedCountries = toCountryOptions(value);
                              const setCountries = (selected: Array<CountryOption>) =>
                                onTreatmentFieldChange(
                                  treatmentIndex,
                                  field.field,
                                  selected.map((option) => option.code).join(','),
                                );
                              const countryPickerProps = {
                                options: countryOptions,
                                getOptionLabel: (option: CountryOption) => option.label,
                                isOptionEqualToValue: (option: CountryOption, selected: CountryOption) =>
                                  option.code === selected.code,
                                sx: { flex: '1 1 220px' },
                                renderInput: (params: AutocompleteRenderInputParams) => (
                                  <TextField
                                    {...params}
                                    label={label}
                                    required={field.required}
                                    helperText={helperText}
                                  />
                                ),
                              };

                              // Two explicit branches: MUI types the value as an array only when
                              // `multiple` is the literal `true`.
                              return field.multiple ? (
                                <Autocomplete
                                  key={field.field}
                                  {...countryPickerProps}
                                  multiple
                                  value={selectedCountries}
                                  onChange={(_, next) => setCountries(next)}
                                />
                              ) : (
                                <Autocomplete
                                  key={field.field}
                                  {...countryPickerProps}
                                  value={selectedCountries[0] ?? null}
                                  onChange={(_, next) => setCountries(next ? [next] : [])}
                                />
                              );
                            }

                            return (
                              <TextField
                                key={field.field}
                                // A closed option set renders as a select so the value can only be
                                // one the contract accepts; free-text fields stay as they are.
                                select={!!field.options}
                                label={label}
                                required={field.required}
                                helperText={helperText}
                                value={value}
                                sx={{ flex: '1 1 220px' }}
                                onChange={(event) =>
                                  onTreatmentFieldChange(treatmentIndex, field.field, event.target.value)
                                }
                              >
                                {field.options?.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </TextField>
                            );
                          })}
                          <Button
                            type="button"
                            color="error"
                            onClick={() => onRemoveTreatment(treatmentIndex)}
                            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                          >
                            Remove
                          </Button>
                        </Stack>
                        {showFieldDescriptions && config?.longDescription ? (
                          <Typography color="text.secondary" variant="body2">
                            {config.longDescription}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Collapse>
                  </Paper>
                );
              })
            ) : (
              <Typography color="text.secondary" variant="body2">
                No treatments configured — this variant shows the visitor nothing.
              </Typography>
            )}
            <Box>
              <Button type="button" size="small" onClick={onAddTreatment}>
                Add treatment
              </Button>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
